/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import ScreenManager from './ScreenManager';
import Topic from './Topic';
import Designer from './Designer';
import ColorUtil from './theme/ColorUtil';
import type { ThemeVariant } from './theme/Theme';
import type { OrientationType } from './layout/LayoutType';
import { $msg } from './Messages';
import LayoutEventBus from './layout/LayoutEventBus';
import NodeModel from './model/NodeModel';

type HelperElements = {
  container: HTMLDivElement;
  tabRow: HTMLDivElement;
  tabKey: HTMLDivElement;
  tabText: HTMLSpanElement;
  enterRow: HTMLDivElement;
  enterKey: HTMLDivElement;
  enterText: HTMLSpanElement;
};

type OverlayGeometry = {
  overlayLeft: number;
  overlayTop: number;
  overlayWidth: number;
  overlayHeight: number;
  overlayRight: number;
  topicLeft: number;
  topicRight: number;
  topicBottom: number;
  topicCenterX: number;
  topicCenterY: number;
  orientation: OrientationType;
  isVertical: boolean;
  isOnLeft: boolean;
};

class HTMLTopicSelected {
  private _overlayContainer: HTMLDivElement | null = null;

  private _overlay: HTMLDivElement | null = null;

  private _helperContainer: HTMLDivElement | null;

  private _helperElements: HelperElements | null = null;

  private _rightPlus: HTMLDivElement | null = null;

  private _bottomPlus: HTMLDivElement | null = null;

  private _screenManager: ScreenManager;

  private _containerElement: HTMLDivElement;

  private _isVisible: boolean;

  private _topic: Topic;

  private _designer: Designer | null;

  constructor(
    topic: Topic,
    containerElement: HTMLDivElement,
    screenManager: ScreenManager,
    designer?: Designer,
  ) {
    this._topic = topic;
    this._containerElement = containerElement;
    this._screenManager = screenManager;
    this._designer = designer || null;
    this._isVisible = false;
    this._helperContainer = null;

    // Create overlay container - insert it first so it's behind SVG
    this._overlayContainer = document.createElement('div');
    this._overlayContainer.style.position = 'absolute';
    this._overlayContainer.style.top = '0';
    this._overlayContainer.style.left = '0';
    this._overlayContainer.style.width = '100%';
    this._overlayContainer.style.height = '100%';
    // Keep pointerEvents none on container - buttons will be appended to main container instead
    this._overlayContainer.style.pointerEvents = 'none';
    this._overlayContainer.style.zIndex = '0';
    this._overlayContainer.style.overflow = 'visible';
    containerElement.insertBefore(this._overlayContainer, containerElement.firstChild);

    // Create overlay for the topic
    this._overlay = document.createElement('div');
    this._overlay.style.position = 'absolute';
    // Colors will be set by updateColors() method
    this._overlay.style.pointerEvents = 'none'; // Overlay itself doesn't capture clicks
    this._overlay.style.display = 'none'; // Hidden by default
    this._overlay.style.transition = 'none'; // No fade animation - remove instantly
    this._overlayContainer.appendChild(this._overlay);

    // Update colors based on variant
    this.updateColors();

    // Check and update shadow based on topic's selected state
    const updateShadowVisibility = () => {
      // Check if topic still exists before updating
      try {
        if (!this._topic || !this._topic.getModel()) {
          // Topic has been removed - hide overlay and return
          this.hide();
          return;
        }
      } catch {
        // Topic may have been removed - hide overlay and return
        this.hide();
        return;
      }

      // Hide shadow if multiple topics are selected or if this topic is not selected
      try {
        if (!this._topic.isOnFocus()) {
          this.hide();
          return;
        }
      } catch {
        // Topic may have been removed - hide overlay and return
        this.hide();
        return;
      }

      // Check if multiple topics are selected
      if (designer) {
        try {
          const selectedTopics = designer.getModel().filterSelectedTopics();
          if (selectedTopics.length > 1) {
            // Multiple topics selected - hide shadow
            this.hide();
            return;
          }
        } catch {
          // Designer or model may have been removed - hide overlay and return
          this.hide();
          return;
        }
      }

      // Single topic selected and this is it - show shadow
      this.show();
    };

    // Listen to focus events to update shadow visibility
    topic.addEvent('ontfocus', () => {
      updateShadowVisibility();
    });

    // Listen to blur events to update shadow visibility
    topic.addEvent('ontblur', () => {
      updateShadowVisibility();
    });

    // Also listen to mousedown to ensure focus is set when clicking
    // (onObjectFocusEvent only deselects others, doesn't select the clicked topic)
    topic.addEvent('mousedown', () => {
      // Check if this topic is not already focused, then focus it
      if (!topic.isOnFocus()) {
        topic.setOnFocus(true);
        if (designer) {
          designer.onObjectFocusEvent(topic);
        }
      }
    });

    // Check initial state - if topic is already selected, show shadow
    updateShadowVisibility();
  }

  private isTopicOnLeftSide(): boolean {
    // If this is the central topic, default to right (start rendering to the right)
    if (this._topic.isCentralTopic()) {
      return false;
    }

    // Find central topic - try walking up parent chain first (for connected topics)
    let centralTopic: Topic | null = this._topic.getParent();
    while (centralTopic && !centralTopic.isCentralTopic()) {
      centralTopic = centralTopic.getParent();
    }

    // If not found via parent chain (isolated topic), search all topics
    if (!centralTopic && this._designer) {
      const allTopics = this._designer.getModel().getTopics();
      centralTopic = allTopics.find((t) => t.isCentralTopic()) || null;
    }

    // If we still couldn't find central topic, default to left
    if (!centralTopic) {
      return true;
    }

    // Use the same logic as the layout manager's _getRelativeDirection method:
    // AbstractBasicSorter._getRelativeDirection: offset = position.x - reference.x
    // Returns 1 (right) if offset >= 0, -1 (left) if offset < 0
    // So: topic.x < central.x → left side (true), topic.x >= central.x → right side (false)
    // This matches the logic used in BalancedSorter: order = position.x > rootNode.getPosition().x ? 0 : 1
    try {
      const topicPosition = this._topic.getPosition();
      const centralPosition = centralTopic.getPosition();

      // If topic X is less than central X, it's on the left side
      return topicPosition.x < centralPosition.x;
    } catch {
      // Fallback: default to left if we can't determine
      return true;
    }
  }

  /**
   * Convert hex color to rgba string
   * Supports both 3-digit (#RGB) and 6-digit (#RRGGBB) hex colors
   */
  private hexToRgba(hex: string, alpha: number): string {
    // Remove # if present
    let cleanedHex = hex.replace('#', '');

    // Handle 3-digit hex colors (e.g., #FFF -> #FFFFFF)
    if (cleanedHex.length === 3) {
      cleanedHex = cleanedHex
        .split('')
        .map((char) => char + char)
        .join('');
    }

    // Parse RGB values
    const r = parseInt(cleanedHex.substring(0, 2), 16);
    const g = parseInt(cleanedHex.substring(2, 4), 16);
    const b = parseInt(cleanedHex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Update colors based on topic variant
   */
  private updateColors(): boolean {
    // Don't update colors if overlay doesn't exist (read-only mode)
    if (!this._overlay) {
      return false;
    }

    const topicColors = this.withTopic((topic) => {
      const variant = topic.getThemeVariant();
      const connectionColor = topic.getConnectionColor(variant);
      return { variant, connectionColor };
    });
    if (!topicColors) {
      return false;
    }

    const { variant, connectionColor } = topicColors;
    // Border color: use connection color directly (3px)
    this._overlay.style.border = `3px solid ${connectionColor}`;

    // Background color: create a semi-transparent version based on variant
    let backgroundColor: string;
    if (variant === 'dark') {
      // For dark mode: lighten the connection color and use with low opacity for dark background
      const lightenedColor = ColorUtil.lightenColor(connectionColor, 30);
      backgroundColor = this.hexToRgba(lightenedColor, 0.25);
    } else {
      // For light mode: lighten the connection color significantly and use with higher opacity
      const lightenedColor = ColorUtil.lightenColor(connectionColor, 80);
      backgroundColor = this.hexToRgba(lightenedColor, 0.95);
    }
    this._overlay.style.backgroundColor = backgroundColor;

    // Plus button colors: use connection color, lightened for better contrast
    const plusButtonColor = ColorUtil.lightenColor(connectionColor, 40);
    // Hover color: lighten even more to signal interactivity
    const plusButtonHoverColor = ColorUtil.lightenColor(connectionColor, 60);

    if (this._rightPlus) {
      this._rightPlus.style.backgroundColor = plusButtonColor;
      this.setupHoverEffect(this._rightPlus, plusButtonColor, plusButtonHoverColor);
    }
    if (this._bottomPlus) {
      this._bottomPlus.style.backgroundColor = plusButtonColor;
      this.setupHoverEffect(this._bottomPlus, plusButtonColor, plusButtonHoverColor);
    }

    // Update helper text colors based on variant
    this.updateHelperTextColors(variant);
    return true;
  }

  /**
   * Update helper text colors based on theme variant
   */
  private updateHelperTextColors(variant: ThemeVariant): void {
    if (!this._helperContainer) {
      return;
    }

    if (!this._helperElements) {
      return;
    }

    const { tabKey, tabText, enterKey, enterText } = this._helperElements;
    const applyColors = (
      key: HTMLDivElement,
      text: HTMLSpanElement,
      keyStyles: { backgroundColor: string; borderColor: string; color: string },
      textColor: string,
    ) => {
      key.style.backgroundColor = keyStyles.backgroundColor;
      key.style.borderColor = keyStyles.borderColor;
      key.style.color = keyStyles.color;
      text.style.color = textColor;
    };

    if (variant === 'dark') {
      applyColors(
        tabKey,
        tabText,
        { backgroundColor: '#3a3a3a', borderColor: '#555', color: '#e0e0e0' },
        '#b0b0b0',
      );
      applyColors(
        enterKey,
        enterText,
        { backgroundColor: '#3a3a3a', borderColor: '#555', color: '#e0e0e0' },
        '#b0b0b0',
      );
    } else {
      applyColors(
        tabKey,
        tabText,
        { backgroundColor: '#f5f5f5', borderColor: '#ddd', color: '#333' },
        '#666',
      );
      applyColors(
        enterKey,
        enterText,
        { backgroundColor: '#f5f5f5', borderColor: '#ddd', color: '#333' },
        '#666',
      );
    }
  }

  private computeOverlayGeometry(topic: Topic): OverlayGeometry | null {
    const corners = topic.getAbsoluteCornerCoordinates();
    if (!corners) {
      return null;
    }

    const orientation = topic.getOrientation();
    const isVertical = orientation === 'vertical';
    const isOnLeft = this.isTopicOnLeftSide();

    // Use ScreenManager's getContainerPosition for consistency
    const containerPosition = this._screenManager.getContainerPosition();

    // Calculate position relative to container
    const left = corners.topLeft.x - containerPosition.left;
    const top = corners.topLeft.y - containerPosition.top;
    const width = corners.topRight.x - corners.topLeft.x;
    const height = corners.bottomLeft.y - corners.topLeft.y;

    // Make overlay 5px bigger (2.5px on each side)
    const padding = 2.5;
    const verticalOffset = 3;
    const horizontalOffset = 2;
    const overlayWidth = width + padding * 2;
    const overlayHeight = height + padding * 2;
    const overlayLeft = left - padding - horizontalOffset;
    const overlayTop = top - padding - verticalOffset;

    return {
      overlayLeft,
      overlayTop,
      overlayWidth,
      overlayHeight,
      overlayRight: overlayLeft + overlayWidth,
      topicLeft: left,
      topicRight: left + width,
      topicBottom: top + height,
      topicCenterX: left + width / 2,
      topicCenterY: top + height / 2,
      orientation,
      isVertical,
      isOnLeft,
    };
  }

  private updateOverlay(): void {
    const geometry = this.withTopic((topic) => this.computeOverlayGeometry(topic));
    if (!geometry) {
      if (this._overlay) {
        this._overlay.style.display = 'none';
      }
      return;
    }

    const hasChildren = this.withTopic((topic) => topic.getChildren().length > 0) ?? false;

    const colorsUpdated = this.updateColors();
    if (colorsUpdated === false) {
      if (this._overlay) {
        this._overlay.style.display = 'none';
      }
      return;
    }

    if (this._overlay) {
      this._overlay.style.display = 'block';
      this.applyOverlayFrame(geometry);
    }
    this.updateHelperBox(geometry, hasChildren);
    this.ensurePlusButtons();
    this.updatePlusButtons(geometry, hasChildren);
  }

  private applyOverlayFrame({
    overlayLeft,
    overlayTop,
    overlayWidth,
    overlayHeight,
  }: OverlayGeometry): void {
    if (!this._overlay) {
      return;
    }

    this._overlay.style.left = `${Math.round(overlayLeft)}px`;
    this._overlay.style.top = `${Math.round(overlayTop)}px`;
    this._overlay.style.width = `${Math.round(overlayWidth)}px`;
    this._overlay.style.height = `${Math.round(overlayHeight)}px`;

    const minDimension = Math.min(overlayWidth, overlayHeight);
    const borderRadiusValue = Math.max(15, minDimension * 0.2);
    const borderRadius = `${borderRadiusValue}px`;

    this._overlay.style.borderTopLeftRadius = '0px';
    this._overlay.style.borderTopRightRadius = '0px';
    this._overlay.style.borderBottomLeftRadius = '0px';
    this._overlay.style.borderBottomRightRadius = '0px';

    this._overlay.style.borderTopLeftRadius = borderRadius;
    this._overlay.style.borderBottomRightRadius = borderRadius;
  }

  private ensureHelperElements(): HelperElements | null {
    if (this._helperElements) {
      return this._helperElements;
    }

    if (!this._overlayContainer) {
      return null;
    }

    const helperContainer = document.createElement('div');
    helperContainer.style.position = 'absolute';
    helperContainer.style.pointerEvents = 'none';
    helperContainer.style.zIndex = '1000';
    helperContainer.style.transition = 'none';
    helperContainer.style.display = 'flex';
    helperContainer.style.flexDirection = 'column';
    helperContainer.style.gap = '8px';
    helperContainer.style.alignItems = 'flex-start';
    helperContainer.style.justifyContent = 'flex-start';

    const createHelperRow = () => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '8px';
      row.style.justifyContent = 'flex-start';
      row.style.flexDirection = 'row';
      row.style.textAlign = 'left';
      return row;
    };

    const createKey = (label: string) => {
      const key = document.createElement('div');
      key.textContent = label;
      key.style.padding = '4px 8px';
      key.style.border = '1px solid';
      key.style.borderRadius = '4px';
      key.style.fontSize = '12px';
      key.style.fontFamily = 'Arial, sans-serif';
      key.style.display = 'flex';
      key.style.alignItems = 'center';
      key.style.justifyContent = 'center';
      key.style.lineHeight = '1';
      key.style.whiteSpace = 'nowrap';
      return key;
    };

    const createText = (textContent: string) => {
      const text = document.createElement('span');
      text.textContent = textContent;
      text.style.fontSize = '14px';
      text.style.fontFamily = 'Arial, sans-serif';
      text.style.display = 'flex';
      text.style.alignItems = 'center';
      text.style.lineHeight = '1';
      text.style.whiteSpace = 'nowrap';
      return text;
    };

    const tabRow = createHelperRow();
    const tabKey = createKey('Tab');
    const tabText = createText($msg('TAB_TO_CREATE_CHILD'));
    tabRow.appendChild(tabKey);
    tabRow.appendChild(tabText);

    const enterRow = createHelperRow();
    const enterKey = createKey('Enter');
    const enterText = createText($msg('ENTER_TO_CREATE_SIBLING'));
    enterRow.appendChild(enterKey);
    enterRow.appendChild(enterText);

    helperContainer.appendChild(tabRow);
    helperContainer.appendChild(enterRow);
    this._overlayContainer.appendChild(helperContainer);

    this._helperContainer = helperContainer;
    this._helperElements = {
      container: helperContainer,
      tabRow,
      tabKey,
      tabText,
      enterRow,
      enterKey,
      enterText,
    };

    const variant = this.withTopic((topic) => topic.getThemeVariant());
    if (variant) {
      this.updateHelperTextColors(variant);
    }

    return this._helperElements;
  }

  private setHelperRowLayout(
    row: HTMLDivElement,
    direction: 'row' | 'row-reverse',
    justify: 'flex-start' | 'flex-end' | 'center',
    textAlign: 'left' | 'right' | 'center',
  ): void {
    row.style.flexDirection = direction;
    row.style.justifyContent = justify;
    row.style.textAlign = textAlign;
  }

  private updateHelperBox(geometry: OverlayGeometry, hasChildren: boolean): void {
    const helper = this.ensureHelperElements();
    if (!helper) {
      return;
    }

    const { container, tabRow, enterRow } = helper;
    // Hide helper text if topic has children
    container.style.display = hasChildren ? 'none' : 'flex';

    if (hasChildren) {
      return;
    }

    const plusButtonDistance = 26; // Same as plus button distance
    const plusButtonDistanceBottom = 11; // Same as bottom plus button distance
    const helperHorizontalGap = 13; // Gap between plus button and helper text
    const helperVerticalGap = 13; // Gap between plus button and helper text

    if (geometry.isVertical) {
      // For vertical layout, center helper text horizontally on the bottom plus button
      container.style.flexDirection = 'column';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'flex-start';
      // Position below the bottom plus button, centered horizontally
      const bottomButtonTop = geometry.topicBottom + plusButtonDistanceBottom;
      container.style.top = `${bottomButtonTop + helperVerticalGap}px`;
      // Center horizontally on the topic center (where the bottom plus button is)
      container.style.left = `${geometry.topicCenterX}px`;
      container.style.right = 'auto';
      container.style.transform = 'translateX(-50%)'; // Center horizontally
      this.setHelperRowLayout(tabRow, 'row', 'center', 'center');
      this.setHelperRowLayout(enterRow, 'row', 'center', 'center');
    } else {
      // For horizontal layout, center helper text vertically on the right/left plus button
      container.style.flexDirection = 'column';
      container.style.alignItems = geometry.isOnLeft ? 'flex-end' : 'flex-start';
      container.style.justifyContent = 'center'; // Center vertically
      // Position at the same vertical center as the plus button
      container.style.top = `${geometry.topicCenterY}px`;
      const helperOffset = plusButtonDistance + helperHorizontalGap;
      if (geometry.isOnLeft) {
        container.style.left = `${geometry.topicLeft - helperOffset}px`;
        container.style.right = 'auto';
        container.style.transform = 'translateX(-100%) translateY(-50%)'; // Center vertically
      } else {
        container.style.left = `${geometry.topicRight + helperOffset}px`;
        container.style.right = 'auto';
        container.style.transform = 'translateY(-50%)'; // Center vertically
      }
      const direction = geometry.isOnLeft ? 'row-reverse' : 'row';
      const justify = geometry.isOnLeft ? 'flex-end' : 'flex-start';
      const textAlign = geometry.isOnLeft ? 'right' : 'left';
      this.setHelperRowLayout(tabRow, direction, justify, textAlign);
      this.setHelperRowLayout(enterRow, direction, justify, textAlign);
    }
  }

  /**
   * Create SVG plus icon
   */
  private createPlusIcon(): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.style.display = 'block';
    svg.style.fill = 'currentColor';
    // Allow pointer events to pass through to parent for tooltip
    svg.style.pointerEvents = 'none';

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute(
      'd',
      'M8,2 C8.55228475,2 9,2.45576096 9,3.00247329 L9,7 L12.9975267,7 C13.5116309,7 13.9353477,7.38272018 13.9932556,7.88266744 L14,8 ' +
        'C14,8.55228475 13.544239,9 12.9975267,9 L9,9 L9,12.9975267 C9,13.5116309 8.61727982,13.9353477 8.11733256,13.9932556 L8,14 ' +
        'C7.44771525,14 7,13.544239 7,12.9975267 L7,9 L3.00247329,9 C2.44882258,9 2,8.55613518 2,8 C2,7.44771525 2.45576096,7 3.00247329,7 ' +
        'L7,7 L7,3.00247329 C7,2.48836906 7.38272018,2.06465235 7.88266744,2.00674437 L8,2 Z',
    );

    svg.appendChild(path);
    return svg;
  }

  /**
   * Set up hover effect for plus buttons
   */
  private setupHoverEffect(button: HTMLDivElement, baseColor: string, hoverColor: string): void {
    // Use a data attribute to track if hover is set up to avoid duplicate listeners
    if (button.dataset.hoverSetup === 'true') {
      // Update stored colors on the button for touch feedback
      const buttonWithColors = button as HTMLDivElement & {
        _baseColor?: string;
        _hoverColor?: string;
      };
      buttonWithColors._baseColor = baseColor;
      buttonWithColors._hoverColor = hoverColor;
      // Ensure current color is the base color when not hovering
      if (button.style.backgroundColor !== hoverColor) {
        button.style.backgroundColor = baseColor;
      }
      return;
    }

    // Store colors on the button element for access in event handlers and touch feedback
    const buttonWithColors = button as HTMLDivElement & {
      _baseColor?: string;
      _hoverColor?: string;
    };
    buttonWithColors._baseColor = baseColor;
    buttonWithColors._hoverColor = hoverColor;

    button.addEventListener('mouseenter', () => {
      button.style.backgroundColor = hoverColor;
      button.style.transition = 'background-color 0.2s ease';
    });

    button.addEventListener('mouseleave', () => {
      button.style.backgroundColor = baseColor;
    });

    button.dataset.hoverSetup = 'true';
  }

  /**
   * Helper function to handle both click and touch events for plus buttons
   * Prevents duplicate triggers by tracking if action was already handled
   */
  private addPlusButtonHandlers(button: HTMLDivElement, handler: () => void): void {
    let touchHandled = false;

    // Get hover color for touch feedback
    const getHoverColor = (): string | undefined => {
      const buttonWithColors = button as HTMLDivElement & {
        _baseColor?: string;
        _hoverColor?: string;
      };
      return buttonWithColors._hoverColor;
    };

    const getBaseColor = (): string | undefined => {
      const buttonWithColors = button as HTMLDivElement & {
        _baseColor?: string;
        _hoverColor?: string;
      };
      return buttonWithColors._baseColor;
    };

    // Handle click events (desktop and mobile fallback)
    button.addEventListener('click', (event) => {
      // If touch was already handled, ignore click (prevents double-trigger)
      if (touchHandled) {
        touchHandled = false;
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      handler();
    });

    // Handle touch events (mobile)
    button.addEventListener('touchstart', (event) => {
      // Stop propagation but don't prevent default to allow normal touch behavior
      event.stopPropagation();
      // Show hover color for visual feedback
      const hoverColor = getHoverColor();
      if (hoverColor) {
        button.style.backgroundColor = hoverColor;
        button.style.transition = 'background-color 0.1s ease';
      }
    });

    button.addEventListener('touchend', (event) => {
      event.stopPropagation();
      event.preventDefault();
      touchHandled = true;
      handler();
      // Reset to base color after a short delay
      const baseColor = getBaseColor();
      if (baseColor) {
        setTimeout(() => {
          button.style.backgroundColor = baseColor;
        }, 150);
      }
      // Reset flag after a short delay to allow click event to check it
      setTimeout(() => {
        touchHandled = false;
      }, 300);
    });

    // Reset flag and color if touch is cancelled (e.g., user swipes away)
    button.addEventListener('touchcancel', () => {
      touchHandled = false;
      const baseColor = getBaseColor();
      if (baseColor) {
        button.style.backgroundColor = baseColor;
      }
    });
  }

  private ensurePlusButtons(): void {
    if (this._rightPlus && !this._rightPlus.parentElement) {
      this._rightPlus = null;
    }
    if (this._bottomPlus && !this._bottomPlus.parentElement) {
      this._bottomPlus = null;
    }

    if (!this._rightPlus) {
      const rightPlus = document.createElement('div');
      const plusIcon = this.createPlusIcon();
      rightPlus.appendChild(plusIcon);
      rightPlus.style.position = 'absolute';
      rightPlus.style.left = '0px';
      rightPlus.style.top = '0px';
      rightPlus.style.width = '20px';
      rightPlus.style.height = '20px';
      rightPlus.style.borderRadius = '50%';
      rightPlus.style.color = 'white';
      rightPlus.style.display = 'flex';
      rightPlus.style.alignItems = 'center';
      rightPlus.style.justifyContent = 'center';
      rightPlus.style.padding = '0';
      rightPlus.style.margin = '0';
      rightPlus.style.boxSizing = 'border-box';
      rightPlus.style.pointerEvents = 'auto';
      rightPlus.style.cursor = 'pointer';
      rightPlus.style.zIndex = '1001';
      rightPlus.style.transformOrigin = 'center center';
      rightPlus.style.transform = 'translate(-50%, -50%)';
      this.addPlusButtonHandlers(rightPlus, () => {
        const isVertical =
          this.withTopic((topic) => topic.getOrientation() === 'vertical') ?? false;
        if (isVertical) {
          this._createSibling();
        } else {
          this._createChild();
        }
      });
      this._containerElement.appendChild(rightPlus);
      this._rightPlus = rightPlus;
      this.updateColors();
      this.updatePlusButtonTooltips();
    }

    if (!this._bottomPlus) {
      const bottomPlus = document.createElement('div');
      const plusIcon = this.createPlusIcon();
      bottomPlus.appendChild(plusIcon);
      bottomPlus.style.position = 'absolute';
      bottomPlus.style.left = '0px';
      bottomPlus.style.top = '0px';
      bottomPlus.style.width = '20px';
      bottomPlus.style.height = '20px';
      bottomPlus.style.borderRadius = '50%';
      bottomPlus.style.color = 'white';
      bottomPlus.style.display = 'flex';
      bottomPlus.style.alignItems = 'center';
      bottomPlus.style.justifyContent = 'center';
      bottomPlus.style.padding = '0';
      bottomPlus.style.margin = '0';
      bottomPlus.style.boxSizing = 'border-box';
      bottomPlus.style.pointerEvents = 'auto';
      bottomPlus.style.cursor = 'pointer';
      bottomPlus.style.zIndex = '1001';
      bottomPlus.style.transformOrigin = 'center center';
      bottomPlus.style.transform = 'translateX(-50%)';
      this.addPlusButtonHandlers(bottomPlus, () => {
        const isVertical =
          this.withTopic((topic) => topic.getOrientation() === 'vertical') ?? false;
        if (isVertical) {
          this._createChild();
        } else {
          this._createSibling();
        }
      });
      this._containerElement.appendChild(bottomPlus);
      this._bottomPlus = bottomPlus;
      this.updateColors();
      this.updatePlusButtonTooltips();
    }
  }

  /**
   * Update tooltips for plus buttons based on orientation
   */
  private updatePlusButtonTooltips(): void {
    const isVertical = this.withTopic((topic) => topic.getOrientation() === 'vertical') ?? false;

    if (this._rightPlus) {
      // Right button: creates child in horizontal, sibling in vertical
      const tooltip = isVertical
        ? $msg('PLUS_TOOLTIP_CREATE_SIBLING')
        : $msg('PLUS_TOOLTIP_CREATE_CHILD');
      // Set title attribute for native browser tooltip
      this._rightPlus.setAttribute('title', tooltip);
      // Also set aria-label for accessibility
      this._rightPlus.setAttribute('aria-label', tooltip);
    }

    if (this._bottomPlus) {
      // Bottom button: creates sibling in horizontal, child in vertical
      const tooltip = isVertical
        ? $msg('PLUS_TOOLTIP_CREATE_CHILD')
        : $msg('PLUS_TOOLTIP_CREATE_SIBLING');
      // Set title attribute for native browser tooltip
      this._bottomPlus.setAttribute('title', tooltip);
      // Also set aria-label for accessibility
      this._bottomPlus.setAttribute('aria-label', tooltip);
    }
  }

  private updatePlusButtons(geometry: OverlayGeometry, hasChildren: boolean): void {
    const plusButtonDistance = 26; // 26px distance from topic edge (for right/left buttons)
    const plusButtonDistanceBottom = 11; // 11px distance from topic edge (for bottom button)

    if (this._rightPlus) {
      if (geometry.isOnLeft) {
        // Position 26px from the left edge of the topic
        this._rightPlus.style.left = `${geometry.topicLeft - plusButtonDistance}px`;
        this._rightPlus.style.right = 'auto';
      } else {
        // Position 26px from the right edge of the topic
        this._rightPlus.style.left = `${geometry.topicRight + plusButtonDistance}px`;
        this._rightPlus.style.right = 'auto';
      }
      // Center vertically on the topic - use exact center without rounding for precision
      this._rightPlus.style.top = `${geometry.topicCenterY}px`;
      const shouldDisplay = geometry.isVertical || !hasChildren;

      if (shouldDisplay) {
        this._rightPlus.style.display = 'flex';
        this._rightPlus.style.pointerEvents = 'auto';
        // Use translate(-50%, -50%) to center the button perfectly on the topic center vertically
        // The button is positioned 5px inside the topic edge horizontally
        this._rightPlus.style.transform = 'translate(-50%, -50%)';
        // Update tooltip when button becomes visible
        this.updatePlusButtonTooltips();
      } else {
        this._rightPlus.style.display = 'none';
        this._rightPlus.style.pointerEvents = 'none';
      }
    }

    if (this._bottomPlus) {
      // Center horizontally on the topic - use exact center without rounding for precision
      this._bottomPlus.style.left = `${geometry.topicCenterX}px`;
      // Position 11px from the bottom edge of the topic
      this._bottomPlus.style.top = `${geometry.topicBottom + plusButtonDistanceBottom}px`;
      const shouldDisplay = !geometry.isVertical || !hasChildren;

      if (shouldDisplay) {
        this._bottomPlus.style.display = 'flex';
        this._bottomPlus.style.pointerEvents = 'auto';
        // Use translate(-50%, 0) to center horizontally on the topic center
        // The button is positioned 5px inside the topic from the bottom
        this._bottomPlus.style.transform = 'translate(-50%, 0)';
        // Update tooltip when button becomes visible
        this.updatePlusButtonTooltips();
      } else {
        this._bottomPlus.style.display = 'none';
        this._bottomPlus.style.pointerEvents = 'none';
      }
    }

    // Update tooltips when orientation might have changed
    this.updatePlusButtonTooltips();
  }

  toggle(): void {
    const topicExists = this.withTopic(() => true);
    if (!topicExists) {
      if (this._overlay) {
        this._overlay.style.display = 'none';
      }
      return;
    }

    this._isVisible = !this._isVisible;

    if (this._isVisible) {
      this.updateOverlay();
      if (this._helperContainer) {
        this._helperContainer.style.display = 'flex';
      }
    } else {
      if (this._overlay) {
        this._overlay.style.display = 'none';
      }

      if (this._helperContainer) {
        this._helperContainer.style.display = 'none';
      }

      if (this._rightPlus) {
        this._rightPlus.style.display = 'none';
        this._rightPlus.style.pointerEvents = 'none';
      }
      if (this._bottomPlus) {
        this._bottomPlus.style.display = 'none';
        this._bottomPlus.style.pointerEvents = 'none';
      }
    }
  }

  update(): void {
    // Check topic's selected state and update visibility accordingly
    // Hide if multiple topics are selected
    if (!this._topic.isOnFocus()) {
      if (this._isVisible) {
        this.hide();
      }
      return;
    }

    // Check if multiple topics are selected
    if (this._designer) {
      const selectedTopics = this._designer.getModel().filterSelectedTopics();
      if (selectedTopics.length > 1) {
        // Multiple topics selected - hide shadow
        if (this._isVisible) {
          this.hide();
        }
        return;
      }
    }

    // Single topic selected and this is it - show shadow
    if (!this._isVisible) {
      this.show();
    }
    this.updateOverlay();
  }

  show(): void {
    // Check if topic still exists before showing
    try {
      if (!this._topic || !this._topic.getModel()) {
        // Topic has been removed - hide overlay and return
        if (this._overlay) {
          this._overlay.style.display = 'none';
        }
        return;
      }
    } catch {
      // Topic may have been removed - hide overlay and return
      if (this._overlay) {
        this._overlay.style.display = 'none';
      }
      return;
    }

    if (!this._isVisible) {
      this.toggle();
    }
  }

  hide(): void {
    if (this._isVisible) {
      this.toggle();
    }
  }

  private withTopic<T>(callback: (topic: Topic) => T): T | null {
    try {
      if (!this._topic || !this._topic.getModel()) {
        return null;
      }
      return callback(this._topic);
    } catch {
      return null;
    }
  }

  private _createSibling(): void {
    // Get Designer instance (either passed or from globalThis)
    const designer = this._designer || (globalThis as { designer?: Designer }).designer;
    if (!designer) {
      console.warn('Designer instance not available for creating sibling');
      return;
    }

    // Ensure this topic is selected (same behavior as keyboard shortcuts)
    // The shadow is only shown when topic is focused, but we ensure it's selected
    // to match keyboard behavior exactly
    const selectedTopics = designer.getModel().filterSelectedTopics();
    if (selectedTopics.length === 0 || selectedTopics[0] !== this._topic) {
      // Select this topic if it's not already selected
      designer.onObjectFocusEvent(this._topic);
      this._topic.setOnFocus(true);
    }

    // Create sibling (same as Enter key) - used by bottom button
    designer.createSiblingForSelectedNode();
  }

  private _createChild(): void {
    // Get Designer instance (either passed or from globalThis)
    const designer = this._designer || (globalThis as { designer?: Designer }).designer;
    if (!designer) {
      console.warn('Designer instance not available for creating child');
      return;
    }

    // Ensure this topic is selected (same behavior as keyboard shortcuts)
    // The shadow is only shown when topic is focused, but we ensure it's selected
    // to match keyboard behavior exactly
    const selectedTopics = designer.getModel().filterSelectedTopics();
    if (selectedTopics.length === 0 || selectedTopics[0] !== this._topic) {
      // Select this topic if it's not already selected
      designer.onObjectFocusEvent(this._topic);
      this._topic.setOnFocus(true);
    }

    // Create child (same as Tab key)
    designer.createChildForSelectedNode();
  }

  dispose(): void {
    // Remove all elements in the same lifecycle
    if (this._overlayContainer && this._overlayContainer.parentElement) {
      this._overlayContainer.parentElement.removeChild(this._overlayContainer);
    }

    // Clear references
    this._overlayContainer = null;
    this._overlay = null;

    // Remove plus buttons from main container
    if (this._rightPlus && this._rightPlus.parentElement) {
      this._rightPlus.parentElement.removeChild(this._rightPlus);
    }

    if (this._bottomPlus && this._bottomPlus.parentElement) {
      this._bottomPlus.parentElement.removeChild(this._bottomPlus);
    }

    // Clear references
    this._rightPlus = null;
    this._bottomPlus = null;
    this._helperContainer = null;
    this._helperElements = null;
  }

  /**
   * Clean up all selection shadows for a designer instance
   */
  static cleanupSelectionShadows(designer: Designer): void {
    const selectionShadows = designer.getSelectionShadows();
    selectionShadows.forEach((shadow) => {
      shadow.dispose();
    });
    selectionShadows.clear();
  }

  /**
   * Ensure a shadow exists for a topic (create if it doesn't exist)
   * Called when a topic receives focus
   */
  static ensureTopicShadow(designer: Designer, topic: Topic): void {
    const selectionShadows = designer.getSelectionShadows();
    if (!selectionShadows.has(topic)) {
      const screenManager = designer.getScreenManager();
      const containerElement = designer.getContainer();
      const shadow = new HTMLTopicSelected(topic, containerElement, screenManager, designer);
      selectionShadows.set(topic, shadow);
    }
  }

  /**
   * Initialize selection shadows for a designer instance
   * Sets up event listeners and creates shadows for already-selected topics
   */
  static initializeSelectionShadows(designer: Designer): void {
    // Don't initialize selection shadows in read-only mode
    if (designer.isReadOnly()) {
      return;
    }

    // Selection assistance is enabled by default

    HTMLTopicSelected.cleanupSelectionShadows(designer);

    // Create shadows for topics already selected on map load
    // (setOnFocus only fires events on state change, so already-selected topics won't fire topicSelected)
    designer
      .getModel()
      .filterSelectedTopics()
      .forEach((topic) => HTMLTopicSelected.ensureTopicShadow(designer, topic));

    // Helper to find topic by model
    const findTopicByModel = (nodeModel: NodeModel): Topic | undefined =>
      designer
        .getModel()
        .getTopics()
        .find((t) => t.getModel() === nodeModel);

    const selectionShadows = designer.getSelectionShadows();

    // Update shadows when layout changes (position, size, etc.)
    const updateShadows = () => {
      // Use double requestAnimationFrame to ensure DOM updates are complete
      // This is especially important for zoom changes where coordinates need to be recalculated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          selectionShadows.forEach((shadow) => shadow.update());
        });
      });
    };

    // Lifecycle hooks: create shadow when topic is selected
    LayoutEventBus.addEvent('topicSelected', (nodeModel: NodeModel) => {
      const topic = findTopicByModel(nodeModel);
      if (topic) {
        HTMLTopicSelected.ensureTopicShadow(designer, topic);
      }
      // Update all shadows to handle multiple selection (hide shadows if multiple topics selected)
      updateShadows();
    });

    // Lifecycle hooks: hide shadows when topic is unselected (may reveal single selection)
    LayoutEventBus.addEvent('topicUnselected', () => {
      // Update all shadows to handle selection count changes
      updateShadows();
    });

    // Lifecycle hooks: dispose shadow when topic is removed
    LayoutEventBus.addEvent('topicRemoved', (nodeModel: NodeModel) => {
      const topic = findTopicByModel(nodeModel);
      if (topic && selectionShadows.has(topic)) {
        selectionShadows.get(topic)?.dispose();
        selectionShadows.delete(topic);
      }
    });

    LayoutEventBus.addEvent('forceLayout', updateShadows);
    LayoutEventBus.addEvent('topicResize', updateShadows);
    LayoutEventBus.addEvent('topicMoved', updateShadows);
    LayoutEventBus.addEvent('topicConnected', updateShadows);

    // Update shadows when canvas is panned/dragged or zoomed
    LayoutEventBus.addEvent('canvasPanned', updateShadows);
    LayoutEventBus.addEvent('canvasZoomed', updateShadows);
  }
}

export default HTMLTopicSelected;
